import { useEffect, useState } from "react";
import api from "../lib/api";
import { useBilling } from "../context/BillingContext";
import { toast } from "sonner";
import { Lightbulb, Send, Check, Lock } from "lucide-react";

const AREAS = [
  { k: "mission", l: "Mission" },
  { k: "vision", l: "Vision" },
  { k: "values", l: "Values" },
  { k: "tone", l: "Tone / voice" },
  { k: "best_moment", l: "Best moment" },
  { k: "general", l: "General" },
];

export default function ProfileSuggestions() {
  const { isOrgAdmin, isOrgMember } = useBilling();
  const [items, setItems] = useState([]);
  const [area, setArea] = useState("general");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/profile/suggestions");
      setItems(data || []);
    } catch { setItems([]); }
  };
  useEffect(() => { if (isOrgAdmin || isOrgMember) load(); }, [isOrgAdmin, isOrgMember]);

  // Only render for org users
  if (!isOrgAdmin && !isOrgMember) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post("/profile/suggestions", { area, content: content.trim() });
      setContent("");
      toast.success("Shared with your admin");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const resolve = async (id) => {
    await api.post(`/profile/suggestions/${id}/resolve`);
    load();
  };

  return (
    <section className="mb-12" data-testid="profile-suggestions-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#FFB800] flex items-center justify-center">
          <Lightbulb className="w-5 h-5 text-zinc-900" />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-900 font-bold">{isOrgAdmin ? "Section 03" : "Section 02"}</div>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">{isOrgAdmin ? "Team suggestions inbox" : "Suggest ideas to your admin"}</h2>
        </div>
      </div>
      <div className="swiss-rule mb-6"></div>

      {isOrgMember && (
        <div className="border border-zinc-200 p-5 mb-5 bg-zinc-50" data-testid="suggestion-form-wrap">
          <div className="text-xs text-zinc-700 mb-3 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-zinc-500" />
            Profile editing is admin-only. Share your ideas here and they'll show up in your admin's inbox.
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Area</label>
              <div className="mt-2 flex flex-wrap gap-1">
                {AREAS.map((a) => (
                  <button key={a.k} type="button" data-testid={`suggest-area-${a.k}`} onClick={()=>setArea(a.k)}
                    className={`px-3 py-2 text-xs font-bold tracking-wider uppercase border ${area===a.k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:border-zinc-900"}`}>
                    {a.l}
                  </button>
                ))}
              </div>
            </div>
            <textarea data-testid="suggest-content" value={content} onChange={(e)=>setContent(e.target.value)}
              placeholder="What idea, refinement, or observation should the company profile reflect?"
              className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm min-h-[100px]" />
            <button type="submit" disabled={busy} data-testid="suggest-submit"
              className="bg-[#0033FF] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center gap-2">
              <Send className="w-3.5 h-3.5" /> Share with admin
            </button>
          </form>
        </div>
      )}

      <div className="border border-zinc-200">
        <div className="px-5 py-3 border-b border-zinc-200 text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500">
          {isOrgAdmin ? "From your team" : "Your suggestions"}
        </div>
        {items.length === 0 && <div className="p-6 text-sm text-zinc-500" data-testid="suggestions-empty">{isOrgAdmin ? "No suggestions yet." : "You haven't shared any ideas yet."}</div>}
        {items.map((s) => (
          <div key={s.id} className="p-4 border-b border-zinc-200 last:border-0" data-testid={`suggestion-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0033FF]">{s.area}</span>
                  {isOrgAdmin && <span className="text-xs font-semibold">{s.from_name || s.from_email}</span>}
                  <span className="text-xs text-zinc-500 font-mono ml-auto">{new Date(s.created_at).toLocaleDateString()}</span>
                  {s.status === "resolved" && <span className="text-[10px] tracking-wider uppercase bg-zinc-100 px-2 py-0.5 font-bold text-zinc-700">Resolved</span>}
                </div>
                <div className="text-sm text-zinc-800 whitespace-pre-wrap">{s.content}</div>
              </div>
              {isOrgAdmin && s.status !== "resolved" && (
                <button onClick={()=>resolve(s.id)} data-testid={`resolve-${s.id}`}
                  className="border border-zinc-300 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-flex items-center gap-1">
                  <Check className="w-3 h-3" /> Mark done
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
